import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiMenu, FiPlus, FiSearch, FiArrowRight, FiCalendar, FiDollarSign, FiFilter } from 'react-icons/fi';
import { CreditCard, Phone } from 'lucide-react';
import { format, startOfDay } from 'date-fns';
import { DateRangePicker } from './ui/DateRangePicker';
import { CustomSelect } from './ui/CustomSelect';
import { cpApi } from '../api/api';
import { useTransactionsFilters } from '../context/TransactionsFilterContext';
import { useDashboardFilter } from '../context/useDashboardFilter';
import type { VndQrProfile } from '../types';
import VndQrProfileFormWidget from './VndQrProfileFormWidget';
import VndAddTeamMemberFormWidget from './VndAddTeamMemberFormWidget';
import VndRequestsProfileFilter from './VndRequestsProfileFilter';
import { intlLocaleFor } from '../i18n/config';

const PICK_DIRECTIONS = [
  { value: 'tbank_c2c', label: 'TBank C2C' },
  { value: 'tbank_sbp', label: 'TBank СБП' },
  { value: 'interbank_c2c', label: 'Interbank C2C' },
  { value: 'interbank_sbp', label: 'Interbank СБП' },
];

/** Мобилка: центр под шапкой. Иначе parent ~40px + absolute right-0 → календарь уезжает влево и обрезается */
const DATE_RANGE_POPOVER_MOBILE =
  'fixed left-1/2 top-32 z-[200] w-[min(22rem,calc(100vw-1rem))] -translate-x-1/2 max-h-[min(24rem,calc(100dvh-8rem))] overflow-y-auto overscroll-contain';
const DATE_RANGE_POPOVER_MD_TOP12 =
  'md:absolute md:left-auto md:right-0 md:top-12 md:mt-0 md:max-h-none md:translate-x-0 md:overflow-visible md:w-auto md:z-50';
const DATE_RANGE_POPOVER_MD_MT2 =
  'md:absolute md:left-auto md:right-0 md:top-auto md:mt-2 md:max-h-none md:translate-x-0 md:overflow-visible md:w-auto md:z-50';

/** Модалки под кнопкой в шапке: на мобиле ячейка сетки узкая — иначе right-0 смещает панель влево */
const VND_TEAM_ADD_PANEL_CLASS =
  'fixed left-1/2 top-32 z-[200] w-[min(420px,calc(100vw-1rem))] -translate-x-1/2 max-h-[min(88vh,calc(100dvh-8rem))] overflow-y-auto overflow-x-hidden overscroll-contain rounded-2xl border border-slate-200/95 bg-white text-slate-900 shadow-2xl shadow-slate-900/10 ring-1 ring-slate-200/80 backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/95 dark:text-white dark:shadow-black/40 dark:ring-white/5 md:absolute md:left-auto md:right-0 md:top-full md:mt-2 md:translate-x-0 md:max-h-[min(88vh,calc(100dvh-var(--airex-header-height,4rem)-0.75rem))] md:w-[min(420px,calc(100vw-0.75rem))]';

interface Props {
  onMenuToggle: () => void;
  isRequestsPage?: boolean;
  isSettingsPage?: boolean;
  showCreateQrButton?: boolean;
  showAirexRequestsFilters?: boolean;
  showVndRequestsFilters?: boolean;
  showTeamSearch?: boolean;
  teamSearchQuery?: string;
  onTeamSearchChange?: (query: string) => void;
  showAddTeamMemberButton?: boolean;
  showNewTransactionButton?: boolean;
  onNewTransaction?: () => void;
  statusFilter?: string;
  onStatusFilterChange?: (status: string) => void;
  dateRange?: { from: Date | null; to: Date | null };
  onDateRangeChange?: (range: { from: Date | null; to: Date | null }) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onRefresh?: () => void;
  showTransactionsFilters?: boolean;
  showDashboardDateFilter?: boolean;
  showWithdrawButton?: boolean;
  onWithdraw?: () => void;
  showVndQrProfilesFilters?: boolean;
  showCreateSmsPhoneButton?: boolean;
  isPayoutsPage?: boolean;
  profileId?: string;
  onProfileIdChange?: (value: string) => void;
}

export default function CounterpartyHeader({
  onMenuToggle,
  isSettingsPage,
  showCreateQrButton,
  showAirexRequestsFilters,
  showVndRequestsFilters,
  showTeamSearch,
  teamSearchQuery = '',
  onTeamSearchChange,
  showAddTeamMemberButton,
  showNewTransactionButton,
  onNewTransaction,
  dateRange,
  onDateRangeChange,
  statusFilter = 'all',
  searchQuery = '',
  onSearchChange,
  onRefresh,
  showTransactionsFilters,
  showDashboardDateFilter,
  showWithdrawButton,
  onWithdraw,
  onStatusFilterChange,
  showVndQrProfilesFilters,
  showCreateSmsPhoneButton,
  isPayoutsPage = false,
  profileId = 'all',
  onProfileIdChange,
}: Props) {
  const { t, i18n } = useTranslation();
  const intlLocale = intlLocaleFor(i18n.language);
  const txFilters = useTransactionsFilters();
  const dashboardFilter = useDashboardFilter();
  const [moscowTime, setMoscowTime] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTxDatePicker, setShowTxDatePicker] = useState(false);
  const [showDashboardDatePicker, setShowDashboardDatePicker] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [dashboardPickerValue, setDashboardPickerValue] = useState<{ from: Date | null; to: Date | null }>({ from: null, to: null });
  const headerRef = useRef<HTMLElement | null>(null);

  const [isTableUpdating, setIsTableUpdating] = useState(false);
  const [pickWidgetOpen, setPickWidgetOpen] = useState(false);
  const [pickAmount, setPickAmount] = useState('');
  const [pickDirection, setPickDirection] = useState('');
  const [pickTimeoutSeconds, setPickTimeoutSeconds] = useState('0');
  const [isPicking, setIsPicking] = useState(false);
  const [closestRequests, setClosestRequests] = useState<Array<{ id: number; order_id: string; amount: number; payed_amount?: number; payed_amount_counterparty?: number; bank_name?: string; direction_code?: string }>>([]);
  const [cpSettings, setCpSettings] = useState<{ allowed_directions?: string[]; max_amount_limit?: number | null }>({});

  const [vndQrWidgetOpen, setVndQrWidgetOpen] = useState(false);
  const [vndQrEditProfile, setVndQrEditProfile] = useState<VndQrProfile | null>(null);
  const [vndQrFocusSection, setVndQrFocusSection] = useState<'sms' | 'audit' | null>(null);
  const [vndTeamAddOpen, setVndTeamAddOpen] = useState(false);
  const [vndQrProfilesMode, setVndQrProfilesMode] = useState<'active' | 'deleted'>('active');
  const [vndBulkToggleLoading, setVndBulkToggleLoading] = useState<'on' | 'off' | null>(null);
  const [smsPhoneCreating, setSmsPhoneCreating] = useState(false);
  const [requestProfiles, setRequestProfiles] = useState<VndQrProfile[]>([]);

  const pickDirectionOptions = useMemo(() => {
    const list = cpSettings.allowed_directions?.length
      ? PICK_DIRECTIONS.filter((item) => cpSettings.allowed_directions!.includes(item.value))
      : PICK_DIRECTIONS;
    return [{ value: '', label: t('header.pickAny') }, ...list];
  }, [cpSettings.allowed_directions, t]);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const setVar = () => {
      const h = el.getBoundingClientRect().height;
      if (Number.isFinite(h) && h > 0) {
        document.documentElement.style.setProperty('--airex-header-height', `${Math.round(h)}px`);
      }
    };
    setVar();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(setVar);
      ro.observe(el);
    } else {
      window.addEventListener('resize', setVar);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', setVar);
    };
  }, []);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const moscow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Moscow' }));
      setMoscowTime(moscow.toLocaleTimeString(intlLocale, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [intlLocale]);

  useEffect(() => {
    if (!showDatePicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const picker = document.querySelector('[data-date-range-picker]');
      const btn = target.closest('[data-date-picker-button]');
      if (picker && !picker.contains(target) && !btn) setShowDatePicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  useEffect(() => {
    if (!showTxDatePicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const picker = document.querySelector('[data-tx-date-range-picker]');
      const btn = target.closest('[data-tx-date-picker-button]');
      if (picker && !picker.contains(target) && !btn) setShowTxDatePicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTxDatePicker]);

  useEffect(() => {
    if (!showDashboardDatePicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const picker = document.querySelector('[data-dashboard-date-range-picker]');
      const btn = target.closest('[data-dashboard-date-picker-button]');
      if (picker && !picker.contains(target) && !btn) {
        setShowDashboardDatePicker(false);
        setDashboardPickerValue({ from: null, to: null });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDashboardDatePicker]);

  useEffect(() => {
    if (showAirexRequestsFilters && pickWidgetOpen) {
      cpApi.getProfile().then((cp) => {
        setCpSettings({
          allowed_directions: cp.allowed_directions || [],
          max_amount_limit: cp.max_amount_limit ?? null,
        });
      }).catch(() => {});
    }
  }, [showAirexRequestsFilters, pickWidgetOpen]);

  useEffect(() => {
    if (!showAirexRequestsFilters && !showVndRequestsFilters) return;
    (window as unknown as { setAirexTableUpdating?: (v: boolean) => void }).setAirexTableUpdating = setIsTableUpdating;
    return () => {
      delete (window as unknown as { setAirexTableUpdating?: (v: boolean) => void }).setAirexTableUpdating;
    };
  }, [showAirexRequestsFilters, showVndRequestsFilters]);

  useEffect(() => {
    if (!showVndRequestsFilters) return;
    const onVndUpdating = (e: Event) => {
      const updating = (e as CustomEvent<{ updating?: boolean }>).detail?.updating;
      if (typeof updating === 'boolean') {
        setIsTableUpdating(updating);
      }
    };
    window.addEventListener('vndRequestsUpdating', onVndUpdating);
    return () => window.removeEventListener('vndRequestsUpdating', onVndUpdating);
  }, [showVndRequestsFilters]);

  useEffect(() => {
    if (!showVndRequestsFilters || isPayoutsPage) return;
    let cancelled = false;
    const load = () => {
      void cpApi
        .getVndQrProfiles()
        .then((list) => {
          if (!cancelled) setRequestProfiles(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (!cancelled) setRequestProfiles([]);
        });
    };
    load();
    window.addEventListener('vndQrProfilesChanged', load);
    return () => {
      cancelled = true;
      window.removeEventListener('vndQrProfilesChanged', load);
    };
  }, [showVndRequestsFilters, isPayoutsPage]);

  useEffect(() => {
    if (!showAirexRequestsFilters || !pickWidgetOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const widget = document.getElementById('airex-pick-widget-cp');
      const btn = target.closest('[data-airex-pick-widget-button]');
      if (widget && !widget.contains(target) && !btn) setPickWidgetOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAirexRequestsFilters, pickWidgetOpen]);

  useEffect(() => {
    setShowMobileFilters(false);
  }, [
    showAirexRequestsFilters,
    showVndRequestsFilters,
    showVndQrProfilesFilters,
    showTransactionsFilters,
    showDashboardDateFilter,
    showTeamSearch,
  ]);

  useEffect(() => {
    if (!showCreateQrButton) {
      setVndQrWidgetOpen(false);
      setVndQrEditProfile(null);
      setVndQrFocusSection(null);
    }
  }, [showCreateQrButton]);

  useEffect(() => {
    if (!showVndQrProfilesFilters) return;
    window.dispatchEvent(new CustomEvent('vndQrProfilesViewModeChange', { detail: { mode: vndQrProfilesMode } }));
  }, [showVndQrProfilesFilters, vndQrProfilesMode]);

  useEffect(() => {
    if (!showAddTeamMemberButton) setVndTeamAddOpen(false);
  }, [showAddTeamMemberButton]);

  useEffect(() => {
    if (!showAddTeamMemberButton || !vndTeamAddOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const widget = document.getElementById('vnd-team-add-widget');
      const btn = target.closest('[data-vnd-team-add-button]');
      if (widget && !widget.contains(target) && !btn) setVndTeamAddOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddTeamMemberButton, vndTeamAddOpen]);

  useEffect(() => {
    if (!showCreateQrButton) return;
    const h = (e: Event) => {
      const d = (e as CustomEvent<{ edit?: VndQrProfile; focus?: 'sms' | 'audit' }>).detail;
      if (d?.edit) setVndQrEditProfile(d.edit);
      else setVndQrEditProfile(null);
      setVndQrFocusSection(d?.focus === 'sms' || d?.focus === 'audit' ? d.focus : null);
      setVndQrWidgetOpen(true);
    };
    window.addEventListener('openVndQrProfileWidget', h);
    return () => window.removeEventListener('openVndQrProfileWidget', h);
  }, [showCreateQrButton]);

  useEffect(() => {
    if (!showCreateQrButton || !vndQrWidgetOpen) return;
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setVndQrWidgetOpen(false);
        setVndQrEditProfile(null);
        setVndQrFocusSection(null);
      }
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [showCreateQrButton, vndQrWidgetOpen]);

  useEffect(() => {
    if (!showCreateSmsPhoneButton) {
      setSmsPhoneCreating(false);
      return;
    }
    const onBusy = (e: Event) => {
      const busy = (e as CustomEvent<{ busy?: boolean }>).detail?.busy;
      if (typeof busy === 'boolean') setSmsPhoneCreating(busy);
    };
    window.addEventListener('vndSmsPhonesBusy', onBusy);
    return () => window.removeEventListener('vndSmsPhonesBusy', onBusy);
  }, [showCreateSmsPhoneButton]);

  useEffect(() => {
    const onBulkToggleState = (e: Event) => {
      const detail = (e as CustomEvent<{ is_active?: boolean; loading?: boolean }>).detail;
      if (typeof detail?.loading !== 'boolean') return;
      if (detail.loading) {
        if (detail.is_active === true) setVndBulkToggleLoading('on');
        else if (detail.is_active === false) setVndBulkToggleLoading('off');
      } else {
        setVndBulkToggleLoading(null);
      }
    };
    window.addEventListener('vndQrProfilesBulkToggleState', onBulkToggleState);
    return () => window.removeEventListener('vndQrProfilesBulkToggleState', onBulkToggleState);
  }, []);

  return (
    <header
      ref={headerRef}
      className="vnd-chrome-header relative shrink-0 overflow-visible shadow-sm border-b border-red-400/25 dark:border-white/10 z-50 lg:-ml-px lg:w-[calc(100%+1px)] lg:max-w-none"
    >
      <div className="relative z-10 flex items-center justify-between pl-0 pr-3 py-2.5 gap-2 min-h-[64px] md:py-4 md:min-h-20 md:h-auto flex-shrink-0">
        <div className="relative z-20 flex items-center gap-2 min-w-0 flex-1">
          <button onClick={onMenuToggle} className="p-2 rounded-lg text-white/70 hover:bg-white/10 lg:hidden">
            <FiMenu className="w-5 h-5" />
          </button>
          {(showAirexRequestsFilters ||
            showTransactionsFilters ||
            showDashboardDateFilter ||
            showTeamSearch ||
            showVndRequestsFilters ||
            showVndQrProfilesFilters) && (
            <button
              type="button"
              onClick={() => setShowMobileFilters((v) => !v)}
              className="md:hidden inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs"
            >
              <FiFilter className="w-3.5 h-3.5" />
              {t('header.filters')}
            </button>
          )}
          <div className={`${showMobileFilters ? 'flex' : 'hidden'} md:flex items-center gap-2 min-w-0 flex-wrap`}>
            {showVndQrProfilesFilters && (
              <div
                role="tablist"
                className="relative z-20 inline-flex flex-nowrap items-center gap-1 rounded-lg border border-white/25 bg-white/10 p-0.5"
              >
                {[
                  { value: 'active', label: t('header.qrModeActive') },
                  { value: 'deleted', label: t('header.qrModeDeleted') },
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={vndQrProfilesMode === value}
                    onClick={() => {
                      const mode = value as 'active' | 'deleted';
                      setVndQrProfilesMode(mode);
                      window.dispatchEvent(new CustomEvent('vndQrProfilesViewModeChange', { detail: { mode } }));
                    }}
                    className={`inline-flex items-center justify-center whitespace-nowrap px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors pointer-events-auto ${
                      vndQrProfilesMode === value
                        ? 'bg-white text-slate-900 border-white shadow-sm'
                        : 'bg-transparent text-white border-transparent hover:bg-white/20'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {showTransactionsFilters && txFilters && (
              <>
              <input
                type="text"
                placeholder={t('header.searchTeamPlaceholder')}
                value={txFilters.filters.search}
                onChange={(e) => txFilters.setSearch(e.target.value)}
                className="px-3 py-1.5 text-xs bg-white/10 text-white placeholder-white/50 border-0 rounded-lg focus:ring-2 focus:ring-white/50 focus:outline-none w-full md:w-64 md:min-w-[200px]"
              />
              {txFilters.isResponsible && (
                <CustomSelect
                  value={String(txFilters.filters.memberFilter ?? 'all')}
                  onChange={(value) => txFilters.setMemberFilter(value)}
                  options={[
                    { value: 'all', label: t('header.memberAll') },
                    ...((txFilters.team?.members ?? []).map((m) => ({
                      value: String(m.counterparty_id),
                      label: m.counterparty_name ?? `ID ${m.counterparty_id}`,
                    }))),
                  ]}
                  className="w-full md:w-36"
                />
              )}
              <CustomSelect
                value={String(txFilters.filters.typeFilter ?? 'all')}
                onChange={(value) => txFilters.setTypeFilter(value)}
                options={[
                  { value: 'all', label: t('header.txTypeAll') },
                  { value: 'deposit', label: t('header.txDeposit') },
                  { value: 'withdrawal', label: t('header.txWithdrawal') },
                ]}
                className="w-full md:w-32"
              />
              </>
            )}
            {showTeamSearch && (
              <input
                type="text"
                placeholder={t('header.searchNamePlaceholder')}
                value={teamSearchQuery}
                onChange={(e) => onTeamSearchChange?.(e.target.value)}
                className="px-3 py-1 text-xs bg-white/10 text-white placeholder-white/50 border-0 rounded-lg focus:ring-2 focus:ring-white/50 focus:outline-none w-full md:w-64"
              />
            )}
            {showVndRequestsFilters && (
              <>
                <div className="flex flex-wrap items-center gap-1 w-full md:w-auto">
                  {(isPayoutsPage
                    ? [
                        { value: 'all', label: t('header.statusAll') },
                        { value: 'open', label: t('header.statusOpen') },
                        { value: 'work', label: t('header.statusWork') },
                        { value: 'closed', label: t('header.statusClosed') },
                        { value: 'canceled', label: t('header.statusCanceled') },
                      ]
                    : [
                    { value: 'all', label: t('header.statusAll') },
                    { value: 'open', label: t('header.statusOpen') },
                    { value: 'work', label: t('header.statusWork') },
                    { value: 'pending_canceled', label: t('header.statusPendingCanceled') },
                    { value: 'closed', label: t('header.statusClosed') },
                    { value: 'canceled', label: t('header.statusCanceled') },
                  ]).map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => onStatusFilterChange?.(value)}
                      className={`shrink-0 px-2 py-1 text-xs font-medium rounded-md border transition-colors ${
                        statusFilter === value
                          ? 'bg-white/25 text-white border-white/40 shadow-sm'
                          : 'bg-white/5 text-white/80 border-white/15 hover:bg-white/15 hover:text-white'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="w-full md:w-auto">
                  <input
                    type="text"
                    placeholder={t('header.searchVndRequests')}
                    value={searchQuery}
                    onChange={(e) => {
                      const v = e.target.value;
                      onSearchChange?.(v);
                    }}
                    className="px-3 py-1 text-xs bg-white/10 text-white placeholder-white/50 border-0 rounded-lg focus:ring-2 focus:ring-white/50 focus:outline-none w-full md:w-64"
                  />
                </div>
                {!isPayoutsPage && (
                  <VndRequestsProfileFilter
                    profiles={requestProfiles}
                    value={profileId}
                    onChange={(v) => onProfileIdChange?.(v)}
                    onProfilesChange={setRequestProfiles}
                    dateRange={dateRange}
                    isResponsible={Boolean(txFilters?.isResponsible)}
                    userId={txFilters?.user?.id ?? null}
                  />
                )}
              </>
            )}
            {showAirexRequestsFilters && (
              <>
              <div className="w-full md:w-auto">
                <input
                  type="text"
                  placeholder={t('header.searchCards')}
                  value={searchQuery}
                  onChange={(e) => {
                    const v = e.target.value;
                    onSearchChange?.(v);
                    window.dispatchEvent(new CustomEvent('searchChange', { detail: { search: v } }));
                  }}
                  className="px-3 py-1 text-xs bg-white/10 text-white placeholder-white/50 border-0 rounded-lg focus:ring-2 focus:ring-white/50 focus:outline-none w-full md:w-64"
                />
              </div>
              {txFilters?.isResponsible && txFilters?.team?.members && txFilters.team.members.length > 0 && (
                <div className="w-full md:w-auto">
                  <CustomSelect
                    value={String(txFilters.filters.memberFilter ?? 'all')}
                    onChange={(v) => {
                      txFilters.setMemberFilter(v);
                      window.dispatchEvent(new CustomEvent('airexMemberFilterChange', { detail: { memberId: v } }));
                    }}
                    options={[
                      { value: 'all', label: t('header.statusAll') },
                      ...txFilters.team.members.map((m) => ({
                        value: String(m.counterparty_id),
                        label: m.counterparty_name || m.login || `#${m.counterparty_id}`,
                      })),
                    ]}
                    className="w-full md:min-w-[140px]"
                  />
                </div>
              )}
              </>
            )}
          </div>
        </div>

        {(showAirexRequestsFilters || showTransactionsFilters || showDashboardDateFilter || (showTeamSearch && (showAddTeamMemberButton || showNewTransactionButton)) || showVndRequestsFilters) && (
          <div className={`${showMobileFilters ? 'grid grid-cols-2 gap-2' : 'hidden'} md:flex items-center md:gap-2 flex-shrink-0 w-full md:w-auto md:flex-wrap md:flex-nowrap md:justify-end`}>
            {showDashboardDateFilter && dashboardFilter && (
              <div className="relative">
                <button
                  data-dashboard-date-picker-button
                  type="button"
                  onClick={() => {
                    const open = !showDashboardDatePicker;
                    setShowDashboardDatePicker(open);
                    if (open && dashboardFilter.range.from && dashboardFilter.range.to) {
                      setDashboardPickerValue({
                        from: new Date(dashboardFilter.range.from + 'T00:00:00'),
                        to: new Date(dashboardFilter.range.to + 'T23:59:59'),
                      });
                    } else if (!open) {
                      setDashboardPickerValue({ from: null, to: null });
                    }
                  }}
                  className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors duration-200 ${
                    dashboardFilter.range.from && dashboardFilter.range.to
                      ? 'bg-blue-500 text-white'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  title={t('header.dashboardPeriod')}
                >
                  <FiCalendar className="w-4 h-4" />
                </button>
                {showDashboardDatePicker && (
                  <div
                    data-dashboard-date-range-picker
                    className={`${DATE_RANGE_POPOVER_MOBILE} ${DATE_RANGE_POPOVER_MD_TOP12}`}
                  >
                    <DateRangePicker
                      value={
                        dashboardPickerValue.from !== null || dashboardPickerValue.to !== null
                          ? dashboardPickerValue
                          : {
                              from: dashboardFilter.range.from ? new Date(dashboardFilter.range.from + 'T00:00:00') : null,
                              to: dashboardFilter.range.to ? new Date(dashboardFilter.range.to + 'T23:59:59') : null,
                            }
                      }
                      onChange={(range) => {
                        if (range.from && range.to) {
                          const fromStr = `${range.from.getFullYear()}-${String(range.from.getMonth() + 1).padStart(2, '0')}-${String(range.from.getDate()).padStart(2, '0')}`;
                          const toStr = `${range.to.getFullYear()}-${String(range.to.getMonth() + 1).padStart(2, '0')}-${String(range.to.getDate()).padStart(2, '0')}`;
                          dashboardFilter.setRange(fromStr, toStr);
                          setDashboardPickerValue({ from: null, to: null });
                          setShowDashboardDatePicker(false);
                        } else {
                          setDashboardPickerValue({ from: range.from, to: range.to });
                        }
                      }}
                      onTodayClick={() => {
                        const today = new Date();
                        const to = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                        dashboardFilter.setRange(to, to);
                        setShowDashboardDatePicker(false);
                      }}
                      onResetClick={() => {
                        const now = new Date();
                        const to = now.toISOString().slice(0, 10);
                        const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
                        dashboardFilter.setRange(from, to);
                        setShowDashboardDatePicker(false);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {showTransactionsFilters && txFilters && (
              <div className="relative">
                <button
                  data-tx-date-picker-button
                  onClick={() => setShowTxDatePicker(!showTxDatePicker)}
                  className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors duration-200 ${
                    txFilters.filters.dateRange?.from && txFilters.filters.dateRange?.to
                      ? 'bg-blue-500 text-white'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  title={t('header.dateFilterTitle')}
                >
                  <FiCalendar className="w-4 h-4" />
                </button>
                {showTxDatePicker && (
                  <div
                    data-tx-date-range-picker
                    className={`${DATE_RANGE_POPOVER_MOBILE} ${DATE_RANGE_POPOVER_MD_TOP12}`}
                  >
                    <DateRangePicker
                      value={txFilters.filters.dateRange || { from: null, to: null }}
                      onChange={(range) => {
                        txFilters.setDateRange(range);
                        if (range.from && range.to) setShowTxDatePicker(false);
                      }}
                      onTodayClick={() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const todayEnd = new Date(today);
                        todayEnd.setHours(23, 59, 59, 999);
                        txFilters.setDateRange({ from: today, to: todayEnd });
                        setShowTxDatePicker(false);
                      }}
                      onResetClick={() => {
                        const today = new Date();
                        today.setHours(23, 59, 59, 999);
                        const yesterday = new Date(today);
                        yesterday.setDate(yesterday.getDate() - 1);
                        yesterday.setHours(0, 0, 0, 0);
                        txFilters.setDateRange({ from: yesterday, to: today });
                        setShowTxDatePicker(false);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {showNewTransactionButton && onNewTransaction && (
              <button
                onClick={onNewTransaction}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg hover:from-green-600 hover:to-blue-600 transition-colors shadow-sm"
              >
                <FiDollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium hidden sm:inline">{t('header.newTransaction')}</span>
              </button>
            )}
            {showAddTeamMemberButton && (
              <div className="relative z-[150] w-full sm:w-auto flex justify-end shrink-0">
                <button
                  type="button"
                  data-vnd-team-add-button
                  onClick={() => setVndTeamAddOpen((v) => !v)}
                  className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-colors shadow-sm"
                >
                  <FiPlus className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('header.addMember')}</span>
                </button>
                {vndTeamAddOpen && (
                  <div id="vnd-team-add-widget" className={VND_TEAM_ADD_PANEL_CLASS}>
                    <VndAddTeamMemberFormWidget
                      onClose={() => setVndTeamAddOpen(false)}
                      onSaved={() => {
                        window.dispatchEvent(new CustomEvent('vndTeamChanged'));
                        setVndTeamAddOpen(false);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {showVndRequestsFilters && (
              <>
                <div className="col-span-2 md:col-auto flex flex-row md:flex-col items-center justify-between md:justify-center gap-2">
                  {!isSettingsPage && (
                    <div className="text-xs font-mono text-gray-300 bg-gray-800/50 px-1.5 py-0.5 rounded mb-1">{moscowTime}</div>
                  )}
                  <div className="relative">
                    <button
                      data-date-picker-button
                      type="button"
                      onClick={() => setShowDatePicker(!showDatePicker)}
                      className="px-2 py-1 bg-gray-600/50 hover:bg-gray-600 text-gray-100 text-xs font-medium rounded border border-gray-500/30"
                    >
                      {(() => {
                        if (!dateRange?.from || !dateRange?.to) return format(new Date(), 'dd.MM.yyyy');
                        const today = startOfDay(new Date());
                        const yesterday = new Date(today);
                        yesterday.setDate(yesterday.getDate() - 1);
                        const fromDay = startOfDay(dateRange.from);
                        const toDay = startOfDay(dateRange.to);
                        if (fromDay.getTime() === yesterday.getTime() && toDay.getTime() === today.getTime()) {
                          return format(dateRange.to, 'dd.MM.yyyy');
                        }
                        return `${format(dateRange.from, 'dd.MM.yyyy')} - ${format(dateRange.to, 'dd.MM.yyyy')}`;
                      })()}
                      <svg className="ml-2 -mr-1 h-4 w-4 inline" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {showDatePicker && (
                      <div
                        data-date-range-picker
                        className={`${DATE_RANGE_POPOVER_MOBILE} ${DATE_RANGE_POPOVER_MD_MT2}`}
                      >
                        <DateRangePicker
                          value={dateRange || { from: null, to: null }}
                          onChange={(range) => {
                            onDateRangeChange?.(range);
                            if (range.from && range.to) setShowDatePicker(false);
                          }}
                          onTodayClick={() => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const todayEnd = new Date(today);
                            todayEnd.setHours(23, 59, 59, 999);
                            const range = { from: today, to: todayEnd };
                            onDateRangeChange?.(range);
                            setShowDatePicker(false);
                          }}
                          onResetClick={() => {
                            const today = new Date();
                            today.setHours(23, 59, 59, 999);
                            const yesterday = new Date(today);
                            yesterday.setDate(yesterday.getDate() - 1);
                            yesterday.setHours(0, 0, 0, 0);
                            const range = { from: yesterday, to: today };
                            onDateRangeChange?.(range);
                            setShowDatePicker(false);
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    onRefresh?.();
                    window.dispatchEvent(new CustomEvent('refreshRequests'));
                  }}
                  className="col-span-2 md:col-auto w-full md:w-auto px-3 py-1.5 bg-gray-600/50 hover:bg-gray-600 text-gray-100 text-sm font-medium rounded-md border border-gray-500/30 flex items-center justify-center"
                  title={isTableUpdating ? t('common.refreshing') : t('common.refresh')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${isTableUpdating ? 'animate-spin animate-pulse text-red-400' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </>
            )}
            {showAirexRequestsFilters && showWithdrawButton && onWithdraw && (
              <button
                onClick={onWithdraw}
                className="w-full md:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors shadow-sm"
                title={t('header.createWithdrawTask')}
              >
                <FiDollarSign className="w-4 h-4" />
                <span className="text-sm font-medium">{t('header.withdraw')}</span>
              </button>
            )}
            {showAirexRequestsFilters && (
            <>
            <div className="relative w-full md:w-auto">
              <button
                data-airex-pick-widget-button
                onClick={() => { if (pickWidgetOpen) setClosestRequests([]); setPickWidgetOpen((v) => !v); }}
                className="w-full md:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-colors shadow-sm"
                title={t('header.pickWidgetTitle')}
              >
                <FiSearch className="w-4 h-4" />
                <span className="text-sm font-medium">{t('header.pick')}</span>
              </button>
              {pickWidgetOpen && (
                <div
                  id="airex-pick-widget-cp"
                  className="absolute right-0 top-full mt-2 z-50 w-[min(520px,calc(100vw-1rem))] bg-gray-800 border border-gray-700 rounded-lg shadow-xl"
                >
                  <div className="px-4 py-4">
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-4">
                        <div className="text-xs text-gray-300 mb-1">{t('header.amountRub')}</div>
                        <input
                          value={pickAmount}
                          onChange={(e) => { setPickAmount(e.target.value); setClosestRequests([]); }}
                          placeholder={t('header.amountPlaceholder')}
                          className="w-full px-3 py-2 text-sm bg-white/10 text-white placeholder-white/50 border-0 rounded-lg focus:ring-2 focus:ring-white/50 focus:outline-none"
                          disabled={isPicking}
                          inputMode="decimal"
                        />
                      </div>
                      <div className="col-span-5">
                        <div className="text-xs text-gray-300 mb-1">{t('header.direction')}</div>
                        <CustomSelect
                          value={pickDirection}
                          onChange={(value) => { setPickDirection(value); setClosestRequests([]); }}
                          options={pickDirectionOptions}
                          className="w-full"
                          menuClassName="max-h-64"
                          disabled={isPicking}
                        />
                      </div>
                      <div className="col-span-3">
                        <div className="text-xs text-gray-300 mb-1">{t('header.autoPickSec')}</div>
                        <input
                          value={pickTimeoutSeconds}
                          onChange={(e) => setPickTimeoutSeconds(e.target.value)}
                          placeholder="0"
                          className="w-full px-3 py-2 text-sm bg-white/10 text-white placeholder-white/50 border-0 rounded-lg focus:ring-2 focus:ring-white/50 focus:outline-none"
                          disabled={isPicking}
                          inputMode="numeric"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-xs text-gray-400">{t('header.autoPickHint')}</div>
                      <button
                        onClick={async () => {
                          const amount = Number(String(pickAmount).replace(',', '.'));
                          if (!Number.isFinite(amount) || amount <= 0) {
                            window.dispatchEvent(new CustomEvent('showToast', { detail: { message: t('header.pickErrorAmount'), type: 'error' } }));
                            return;
                          }
                          try {
                            const timeout = Math.max(0, Math.min(300, parseInt(pickTimeoutSeconds || '0', 10) || 0));
                            setIsPicking(true);
                            const resp = await cpApi.pickRequest({
                              amount,
                              direction_code: pickDirection || undefined,
                              timeout_seconds: timeout,
                            });
                            const picked = resp?.data;
                            const airexRequest = picked?.airex_request || picked;
                            const id = airexRequest?.id ?? airexRequest?.ID;
                            const orderId = airexRequest?.order_id ?? airexRequest?.OrderID;
                            const matchId = picked?.match_id;
                            const msg = id
                              ? (matchId
                                  ? t('header.pickSuccessMatch', { order: String(orderId || id), match: String(matchId) })
                                  : t('header.pickSuccessWith', { id: String(orderId || id) }))
                              : t('header.pickSuccess');
                            window.dispatchEvent(new CustomEvent('showToast', { detail: { message: msg, type: 'success' } }));
                            setPickWidgetOpen(false);
                            setPickAmount('');
                            setPickDirection('');
                            setPickTimeoutSeconds('0');
                            setClosestRequests([]);
                            onRefresh?.();
                            window.dispatchEvent(new CustomEvent('refreshRequests'));
                            if (id && matchId) {
                              window.dispatchEvent(new CustomEvent('openPayoutWidget', { detail: { requestId: id, mode: 'payout', matchId, matchAmount: amount } }));
                            }
                          } catch (e: unknown) {
                            const getErr = (): { msg: string; noSuitable: boolean } => {
                              if (typeof e === 'string') return { msg: e, noSuitable: false };
                              const err = e as { response?: { data?: { error?: string }; status?: number }; message?: string };
                              const errData = err?.response?.data;
                              if (errData && typeof errData === 'object' && typeof errData.error === 'string') {
                                if (errData.error.includes('no suitable')) {
                                  return { msg: t('header.noRequisites'), noSuitable: true };
                                }
                                return { msg: errData.error, noSuitable: false };
                              }
                              if (err?.response?.status === 400) {
                                return { msg: t('header.noRequisites'), noSuitable: true };
                              }
                              return { msg: err?.message || t('header.pickError'), noSuitable: false };
                            };
                            const { msg: errMsg, noSuitable } = getErr();
                            window.dispatchEvent(
                              new CustomEvent('showToast', { detail: { message: t('header.errorWithPrefix', { msg: errMsg }), type: 'error' } })
                            );
                            if (noSuitable && amount > 0) {
                              try {
                                const closest = await cpApi.getAvailableAirexRequests({
                                  amount,
                                  tolerance_percent: 50,
                                  limit: 5,
                                });
                                const list = Array.isArray(closest)
                                  ? (closest as Array<{
                                      id: number;
                                      order_id?: string;
                                      amount: number;
                                      payed_amount?: number;
                                      payed_amount_counterparty?: number;
                                      bank_name?: string;
                                      direction_code?: string;
                                    }>)
                                  : [];
                                setClosestRequests(
                                  list.map((r) => ({
                                    id: r.id,
                                    order_id: r.order_id ?? String(r.id),
                                    amount: r.amount,
                                    payed_amount: r.payed_amount,
                                    payed_amount_counterparty: r.payed_amount_counterparty,
                                    bank_name: r.bank_name,
                                    direction_code: r.direction_code,
                                  }))
                                );
                              } catch {
                                setClosestRequests([]);
                              }
                            } else {
                              setClosestRequests([]);
                            }
                          } finally {
                            setIsPicking(false);
                          }
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg hover:from-green-600 hover:to-blue-600 transition-colors shadow-sm disabled:opacity-60"
                        disabled={isPicking}
                        title={t('header.pickAssignTitle')}
                      >
                        {isPicking ? (
                          <span className="flex items-center gap-1">
                            <span className="animate-spin rounded-full h-3 w-3 border-2 border-red-300/40 border-t-red-100" />
                            {t('header.searching')}
                          </span>
                        ) : (
                          <>
                            <FiArrowRight className="w-4 h-4" />
                            <span className="text-sm font-medium">{t('header.start')}</span>
                          </>
                        )}
                      </button>
                    </div>
                    {closestRequests.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-600">
                        <div className="text-xs text-gray-300 mb-2">{t('header.nearestRequests')}</div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {closestRequests.map((r) => {
                            const rem = r.amount - (r.payed_amount ?? 0) - (r.payed_amount_counterparty ?? 0);
                            const isC2C = r.direction_code === 'tbank_c2c' || r.direction_code === 'interbank_c2c';
                            const isSBP = r.direction_code === 'tbank_sbp' || r.direction_code === 'interbank_sbp';
                            return (
                              <div
                                key={r.id}
                                className="flex items-center justify-between gap-2 px-3 py-2 bg-white/5 rounded-lg text-sm"
                              >
                                <div className="min-w-0 flex-1">
                                  <span className="font-medium text-white">#{r.order_id}</span>
                                  <span className="text-gray-400 ml-2">{rem.toLocaleString(intlLocale, { maximumFractionDigits: 0 })} ₽</span>
                                  <span className="text-gray-500 ml-1 truncate">{r.bank_name || ''}</span>
                                  <span className="ml-1">
                                    {isC2C && <CreditCard className="w-3.5 h-3.5 inline text-blue-400" />}
                                    {isSBP && <Phone className="w-3.5 h-3.5 inline text-blue-400" />}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    window.dispatchEvent(new CustomEvent('openConfirmTake', { detail: { request: r } }));
                                    setPickWidgetOpen(false);
                                    setClosestRequests([]);
                                  }}
                                  className="shrink-0 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded"
                                >
                                  {t('header.take')}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="col-span-2 md:col-auto flex flex-row md:flex-col items-center justify-between md:justify-center gap-2">
              {!isSettingsPage && (
                <div className="text-xs font-mono text-gray-300 bg-gray-800/50 px-1.5 py-0.5 rounded mb-1">{moscowTime}</div>
              )}
              <div className="relative">
                <button
                  data-date-picker-button
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="px-2 py-1 bg-gray-600/50 hover:bg-gray-600 text-gray-100 text-xs font-medium rounded border border-gray-500/30"
                >
                  {(() => {
                    if (!dateRange?.from || !dateRange?.to) return format(new Date(), 'dd.MM.yyyy');
                    const today = startOfDay(new Date());
                    const yesterday = new Date(today);
                    yesterday.setDate(yesterday.getDate() - 1);
                    const fromDay = startOfDay(dateRange.from);
                    const toDay = startOfDay(dateRange.to);
                    if (fromDay.getTime() === yesterday.getTime() && toDay.getTime() === today.getTime()) {
                      return format(dateRange.to, 'dd.MM.yyyy');
                    }
                    return `${format(dateRange.from, 'dd.MM.yyyy')} - ${format(dateRange.to, 'dd.MM.yyyy')}`;
                  })()}
                  <svg className="ml-2 -mr-1 h-4 w-4 inline" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                </button>
                {showDatePicker && (
                  <div
                    data-date-range-picker
                    className={`${DATE_RANGE_POPOVER_MOBILE} ${DATE_RANGE_POPOVER_MD_MT2}`}
                  >
                    <DateRangePicker
                      value={dateRange || { from: null, to: null }}
                      onChange={(range) => {
                        onDateRangeChange?.(range);
                        if (range.from && range.to) setShowDatePicker(false);
                        window.dispatchEvent(new CustomEvent('airexDateRangeChange', { detail: { range } }));
                      }}
                      onTodayClick={() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const todayEnd = new Date(today);
                        todayEnd.setHours(23, 59, 59, 999);
                        const range = { from: today, to: todayEnd };
                        onDateRangeChange?.(range);
                        setShowDatePicker(false);
                        window.dispatchEvent(new CustomEvent('airexDateRangeChange', { detail: { range } }));
                      }}
                      onResetClick={() => {
                        const today = new Date();
                        today.setHours(23, 59, 59, 999);
                        const yesterday = new Date(today);
                        yesterday.setDate(yesterday.getDate() - 1);
                        yesterday.setHours(0, 0, 0, 0);
                        const range = { from: yesterday, to: today };
                        onDateRangeChange?.(range);
                        setShowDatePicker(false);
                        window.dispatchEvent(new CustomEvent('airexDateRangeChange', { detail: { range } }));
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                onRefresh?.();
                window.dispatchEvent(new CustomEvent('refreshRequests'));
              }}
              className="col-span-2 md:col-auto w-full md:w-auto px-3 py-1.5 bg-gray-600/50 hover:bg-gray-600 text-gray-100 text-sm font-medium rounded-md border border-gray-500/30 flex items-center justify-center"
              title={isTableUpdating ? t('common.refreshing') : t('common.refresh')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${isTableUpdating ? 'animate-spin animate-pulse text-red-400' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            </>
            )}
          </div>
        )}
        {showCreateQrButton && (
          <div className="relative z-10 ml-auto flex w-auto shrink-0 flex-nowrap items-center justify-end gap-2">
            <div className="flex w-auto flex-nowrap items-center gap-2 rounded-xl border border-white/20 bg-white/10 p-1">
              <button
                type="button"
                disabled={vndBulkToggleLoading !== null}
                onClick={() => window.dispatchEvent(new CustomEvent('vndQrProfilesSetAllActive', { detail: { is_active: true } }))}
                className={`flex items-center justify-center whitespace-nowrap px-3 py-2 bg-emerald-500/85 text-white rounded-lg transition-colors shadow-sm border border-emerald-300/40 ${
                  vndBulkToggleLoading !== null ? 'opacity-60 cursor-not-allowed' : 'hover:bg-emerald-500'
                }`}
              >
                {vndBulkToggleLoading === 'on' ? (
                  <span className="inline-flex items-center gap-2 text-sm font-medium">
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    {t('header.enabling')}
                  </span>
                ) : (
                  <span className="text-sm font-medium">{t('header.enableAll')}</span>
                )}
              </button>
              <button
                type="button"
                disabled={vndBulkToggleLoading !== null}
                onClick={() => window.dispatchEvent(new CustomEvent('vndQrProfilesSetAllActive', { detail: { is_active: false } }))}
                className={`flex items-center justify-center whitespace-nowrap px-3 py-2 bg-rose-500/80 text-white rounded-lg transition-colors shadow-sm border border-rose-300/40 ${
                  vndBulkToggleLoading !== null ? 'opacity-60 cursor-not-allowed' : 'hover:bg-rose-500'
                }`}
              >
                {vndBulkToggleLoading === 'off' ? (
                  <span className="inline-flex items-center gap-2 text-sm font-medium">
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    {t('header.disabling')}
                  </span>
                ) : (
                  <span className="text-sm font-medium">{t('header.disableAll')}</span>
                )}
              </button>
            </div>
            <button
              type="button"
              data-vnd-qr-create-button
              onClick={() => {
                setVndQrWidgetOpen((prev) => {
                  if (prev) {
                    setVndQrEditProfile(null);
                    setVndQrFocusSection(null);
                    return false;
                  }
                  setVndQrEditProfile(null);
                  setVndQrFocusSection(null);
                  return true;
                });
              }}
              className="flex items-center justify-center space-x-2 whitespace-nowrap px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-colors shadow-sm"
            >
              <FiPlus className="w-4 h-4" />
              <span className="text-sm font-medium">{t('header.createQr')}</span>
            </button>
            {vndQrWidgetOpen && (
              <VndQrProfileFormWidget
                editProfile={vndQrEditProfile}
                focusSection={vndQrFocusSection}
                onClose={() => {
                  setVndQrWidgetOpen(false);
                  setVndQrEditProfile(null);
                  setVndQrFocusSection(null);
                }}
                onSaved={() => window.dispatchEvent(new CustomEvent('vndQrProfilesChanged'))}
              />
            )}
          </div>
        )}
        {showCreateSmsPhoneButton && (
          <div className="relative z-10 ml-auto flex w-auto shrink-0 items-center justify-end">
            <button
              type="button"
              disabled={smsPhoneCreating}
              onClick={() => window.dispatchEvent(new CustomEvent('vndSmsPhonesCreate'))}
              className={`flex items-center justify-center space-x-2 whitespace-nowrap px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg transition-colors shadow-sm ${
                smsPhoneCreating ? 'opacity-60 cursor-not-allowed' : 'hover:from-emerald-600 hover:to-teal-600'
              }`}
            >
              <FiPlus className="w-4 h-4" />
              <span className="text-sm font-medium">{t('smsPhones.create')}</span>
            </button>
          </div>
        )}

      </div>
    </header>
  );
}
